import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import LanguageSelector from "@/components/LanguageSelector";
import { ArrowRight } from "@phosphor-icons/react";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, pw);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md lele-card p-8 md:p-10"
      >
        <div className="mb-6 flex items-center justify-between">
          <BrandLogo size={40} showWordmark />
          <LanguageSelector />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-2">
          {t("brand.tagline_line1")} {t("brand.tagline_line2")}
        </div>
        <h2 className="font-heading text-3xl mb-2">{t("auth.signupTitle")}</h2>
        <p className="text-sm text-neutral-500 mb-8">
          {t("auth.signupSubtitle")}
        </p>

        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
          {t("auth.nameLabel")}
        </label>
        <input
          data-testid={AUTH.registerName}
          type="text"
          required
          className="lele-input w-full rounded-lg px-4 py-3 mb-5"
          placeholder={t("auth.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
          {t("common.email")}
        </label>
        <input
          data-testid={AUTH.registerEmail}
          type="email"
          required
          className="lele-input w-full rounded-lg px-4 py-3 mb-5"
          placeholder={t("auth.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
          {t("common.password")}
        </label>
        <input
          data-testid={AUTH.registerPassword}
          type="password"
          required
          className="lele-input w-full rounded-lg px-4 py-3 mb-8"
          placeholder={t("auth.passwordPlaceholder")}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
        />

        <button
          data-testid={AUTH.registerSubmit}
          type="submit"
          disabled={loading}
          className="lele-btn-primary w-full flex items-center justify-center gap-2"
        >
          {loading ? t("common.loading") : t("auth.signupCta")}
          {!loading && <ArrowRight size={16} weight="bold" />}
        </button>

        <div className="mt-6 text-center text-sm text-neutral-500">
          {t("auth.haveAccount")}{" "}
          <Link
            data-testid={AUTH.registerGoToLogin}
            to="/login"
            className="text-indigo-400 hover:text-indigo-300"
          >
            {t("auth.signIn")}
          </Link>
        </div>
      </form>
    </div>
  );
}
